import { z } from "zod";
import { myStoryTemporalPositionSchema } from "./my-story.js";
import { opaqueIdSchema, sha256Schema } from "./opaque.js";
export const ourStoryPrincipalSchema = z
    .object({
    personId: opaqueIdSchema,
    displayName: z.string().trim().min(1).max(120),
    pronouns: z.string().trim().min(1).max(80),
})
    .strict();
export const ourStoryVisibilitySchema = z.enum([
    "PRIVATE_TO_PRINCIPAL",
    "REVEAL_AFTER_BOTH_ANSWER",
    "REVEALED_TO_PARTNER",
    "EDITOR_ONLY",
    "SHARED_WITH_BOOK",
    "EXCLUDED",
]);
export const ourStoryPocSourceSchema = z
    .object({
    sourceId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    speakerPersonIds: z.array(opaqueIdSchema).min(1).max(3),
    session: z.number().int().min(1).max(20),
    title: z.string().trim().min(1).max(200),
    kind: z.enum(["INDEPENDENT_INTERVIEW", "MEMORY_ROOM", "WITNESS_NOTE", "ARTIFACT_NOTE"]),
    revisionId: opaqueIdSchema,
    revisionNumber: z.number().int().positive(),
    parentRevisionId: opaqueIdSchema.nullable(),
    capturedAt: z.string().datetime(),
    checksumSha256: sha256Schema,
    text: z.string().min(80).max(30_000),
    consentedPersonIds: z.array(opaqueIdSchema).min(1).max(3),
    visibility: ourStoryVisibilitySchema,
    revealedAt: z.string().datetime().nullable(),
    synthetic: z.literal(true),
})
    .strict()
    .superRefine((source, context) => {
    if (new Set(source.speakerPersonIds).size !== source.speakerPersonIds.length)
        context.addIssue({
            code: "custom",
            path: ["speakerPersonIds"],
            message: "Speaker identities must be unique.",
        });
    if (source.speakerPersonIds.some((id) => !source.consentedPersonIds.includes(id)))
        context.addIssue({
            code: "custom",
            path: ["consentedPersonIds"],
            message: "Every speaker must have an exact consent binding.",
        });
    const revealed = source.visibility === "REVEALED_TO_PARTNER";
    if (revealed !== (source.revealedAt !== null))
        context.addIssue({
            code: "custom",
            path: ["revealedAt"],
            message: "Only deliberately revealed sources may carry a reveal timestamp.",
        });
});
export const ourStoryClaimSchema = z
    .object({
    claimId: opaqueIdSchema,
    text: z.string().trim().min(1).max(4_000),
    speakerPersonIds: z.array(opaqueIdSchema).min(1).max(3),
    perspective: z.enum(["MAYA", "ROWAN", "JOINT", "WITNESS"]),
    agreement: z.enum(["INDIVIDUAL", "AGREED", "DIFFERING", "UNRESOLVED"]),
    visibility: ourStoryVisibilitySchema,
    sourceIds: z.array(opaqueIdSchema).min(1).max(20),
    sensitive: z.boolean(),
})
    .strict();
export const ourStoryDifferenceGroupSchema = z
    .object({
    differenceId: opaqueIdSchema,
    topic: z.string().trim().min(1).max(240),
    claimIds: z.array(opaqueIdSchema).min(2).max(10),
    treatment: z.enum([
        "SHARED_EXPLANATION",
        "SIDE_BY_SIDE",
        "MAYA_ONLY",
        "ROWAN_ONLY",
        "PRIVATE",
        "OMIT",
        "REVISIT",
    ]),
    sharedExplanation: z.string().trim().min(1).max(2_000).nullable(),
})
    .strict()
    .superRefine((group, context) => {
    if ((group.treatment === "SHARED_EXPLANATION") !== (group.sharedExplanation !== null))
        context.addIssue({
            code: "custom",
            path: ["sharedExplanation"],
            message: "Only a shared-explanation treatment may contain agreed synthesis.",
        });
});
export const ourStoryEventSchema = z
    .object({
    eventId: opaqueIdSchema,
    title: z.string().trim().min(1).max(200),
    sequence: z.number().int().nonnegative(),
    temporal: myStoryTemporalPositionSchema,
    claimIds: z.array(opaqueIdSchema).min(1).max(100),
})
    .strict();
export const ourStoryChapterPlanSchema = z
    .object({
    chapterId: opaqueIdSchema,
    position: z.number().int().positive().max(20),
    title: z.string().trim().min(1).max(200),
    purpose: z.string().trim().min(1).max(1_000),
    claimIds: z.array(opaqueIdSchema).min(1).max(200),
    primaryEventIds: z.array(opaqueIdSchema).max(40),
    narrativeForms: z
        .array(z.enum(["SHARED_WE", "MAYA_VOICE", "ROWAN_VOICE", "SIDE_BY_SIDE", "JOINT_REFLECTION"]))
        .min(1),
})
    .strict();
export const ourStoryEvidenceFoundationSchema = z
    .object({
    version: z.literal(1),
    bookId: opaqueIdSchema,
    mode: z.enum(["TOGETHER", "FROM_ME_TO_US"]),
    principals: z.array(ourStoryPrincipalSchema).length(2),
    sources: z.array(ourStoryPocSourceSchema).min(4).max(30),
    claims: z.array(ourStoryClaimSchema).min(1).max(2_000),
    events: z.array(ourStoryEventSchema).min(1).max(500),
    differences: z.array(ourStoryDifferenceGroupSchema).max(100),
    chapters: z.array(ourStoryChapterPlanSchema).min(1).max(20),
})
    .strict();
export const ourStoryPocSourcePacketSchema = z
    .object({
    version: z.literal(1),
    bookId: opaqueIdSchema,
    title: z.literal("The Distance Between Two Cups"),
    subtitle: z.literal("Maya & Rowan, In Two Voices"),
    mode: z.literal("TOGETHER"),
    principals: z.array(ourStoryPrincipalSchema).length(2),
    sources: z.array(ourStoryPocSourceSchema).min(6).max(30),
})
    .strict()
    .superRefine((packet, context) => {
    const principalIds = new Set(packet.principals.map(({ personId }) => personId));
    packet.sources.forEach((source, index) => {
        if (source.bookId !== packet.bookId)
            context.addIssue({
                code: "custom",
                path: ["sources", index, "bookId"],
                message: "Every source must belong to the POC book.",
            });
        if (source.kind !== "WITNESS_NOTE" &&
            source.speakerPersonIds.some((id) => !principalIds.has(id)))
            context.addIssue({
                code: "custom",
                path: ["sources", index, "speakerPersonIds"],
                message: "Relationship sources must retain a principal speaker.",
            });
    });
});
//# sourceMappingURL=our-story.js.map